'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Company-name → logo-URL cache, shared across every monogram instance so a
 * given company is looked up at most once per session no matter how many rows
 * render it. History/companion rows are rebuilt from the pool snapshot, which
 * never kept the *resolved* logo (it stores only `{ name }`), so — when opted in
 * via `resolveLogo` — we re-resolve it by name through the same endpoint the
 * live deck uses. `null` is cached too, so a miss isn't retried every render.
 */
const logoCache = new Map<string, string | null>();
const logoInFlight = new Map<string, Promise<string | null>>();

async function resolveCompanyLogo(name: string): Promise<string | null> {
  const key = name.trim().toLowerCase();
  if (!key || key === '?') return null;
  if (logoCache.has(key)) return logoCache.get(key) ?? null;
  const pending = logoInFlight.get(key);
  if (pending) return pending;
  const request = fetch('/api/search/company-lookup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyName: name }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (j?.company?.logoUrl ?? null) as string | null)
    .catch(() => null)
    .then((url) => {
      logoCache.set(key, url);
      logoInFlight.delete(key);
      return url;
    });
  logoInFlight.set(key, request);
  return request;
}

/** Two-letter monogram for a name: first + last word initials, or the first two
 *  letters of a single word. Falls back to "?" for an empty name. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Curated pale-brand monogram swatches. Each entry pairs a tinted square
 * background with an on-brand letter ink, all desaturated to read as a
 * "designed" placeholder rather than a flat gray box. Hues are drawn from the
 * Career Offensive palette (burgundy core + the status accent family) so the
 * fallback always feels native to the brand.
 */
const MONOGRAM_SWATCHES: ReadonlyArray<{ bg: string; ink: string }> = [
  { bg: 'rgba(127, 29, 29, 0.10)', ink: '#7f1d1d' }, // burgundy-core
  { bg: 'rgba(15, 118, 110, 0.10)', ink: '#0f766e' }, // status-open teal
  { bg: 'rgba(180, 83, 9, 0.10)', ink: '#b45309' }, // status-applied amber
  { bg: 'rgba(30, 64, 124, 0.10)', ink: '#1e407c' }, // deep slate-blue
  { bg: 'rgba(112, 26, 117, 0.10)', ink: '#701a75' }, // muted plum
  { bg: 'rgba(15, 76, 129, 0.10)', ink: '#0f4c81' }, // ocean blue
  { bg: 'rgba(146, 64, 14, 0.10)', ink: '#92400e' }, // burnt sienna
  { bg: 'rgba(20, 83, 73, 0.10)', ink: '#145349' }, // pine green
];

/** Stable 32-bit string hash so a company always maps to the same swatches. */
function hashName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0; // keep within 32-bit range
  }
  return Math.abs(hash);
}

const SIZES = {
  sm: { box: 'h-8 w-8', font: '0.9rem' }, // companion list rows
  md: { box: 'h-11 w-11', font: '1.375rem' }, // swipe-card header
} as const;

/**
 * Company avatar shared across the swipe card and every companion list row:
 * the real logo when available, otherwise a deterministic two-tone brand
 * monogram (e.g. "AG"). Extracted from SwipeCard so the card and the rails
 * render the exact same treatment — a tweak here lands everywhere at once.
 */
export function CompanyMonogram({
  logoUrl,
  name,
  size = 'md',
  className,
  resolveLogo = false,
}: {
  logoUrl?: string | null;
  name: string;
  size?: 'sm' | 'md';
  className?: string;
  /** When no `logoUrl` is passed (a history/companion row rebuilt from the pool
   *  snapshot), resolve the logo by company name so the real logo shows instead
   *  of the monogram — the same source the live deck uses. Cached module-wide. */
  resolveLogo?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  // Opt-in name-based resolution for rows that arrive without a logo. Skipped
  // entirely when a `logoUrl` is already present (live cards), so this never
  // fires on the hot path.
  useEffect(() => {
    if (logoUrl || !resolveLogo || !name) return;
    let active = true;
    void resolveCompanyLogo(name).then((url) => {
      if (active && url) setResolvedUrl(url);
    });
    return () => {
      active = false;
    };
  }, [logoUrl, resolveLogo, name]);

  const effectiveUrl = logoUrl ?? resolvedUrl;
  const showImage = Boolean(effectiveUrl) && !failed;
  const box = SIZES[size].box;

  if (showImage) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden border border-border bg-muted',
          box,
          className,
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={effectiveUrl ?? undefined}
          alt={`${name} logo`}
          className="h-full w-full object-contain"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  // Deterministic two-tone monogram. The hash seeds the square's tint; the
  // second letter steps to an adjacent swatch so the pair reads in two
  // harmonious brand hues. Letters are large + extrabold to fill the square.
  const letters = initials(name);
  const seed = hashName(name);
  const primary = MONOGRAM_SWATCHES[seed % MONOGRAM_SWATCHES.length];
  const secondary = MONOGRAM_SWATCHES[(seed + 3) % MONOGRAM_SWATCHES.length];

  return (
    <div
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden border border-border',
        box,
        className,
      )}
      style={{ backgroundColor: primary.bg }}
    >
      <span
        className="font-extrabold leading-none tracking-tight"
        style={{ fontSize: SIZES[size].font }}
      >
        <span style={{ color: primary.ink }}>{letters[0]}</span>
        {letters[1] ? <span style={{ color: secondary.ink }}>{letters[1]}</span> : null}
      </span>
    </div>
  );
}
